import { useState } from 'react'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { Input } from 'components/Input/Input'
import { CheckBox } from 'components/CheckBox/CheckBox'
import { CheckChip } from 'components/CheckBox/CheckChip'
import { ChipsContainer } from 'components/CheckBox/CheckBox.styled'
import { Modal } from 'components/Modal/Modal'
import useModalClose from 'hooks/useModalClose'
import { CircleButton } from 'pages/Shifts/Shifts.styled'
import { CursorPointer, Flex, colors } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { Card, CardBody } from 'components/Card/Card'
import { IFoodKitchen } from 'types/foodLogistics/foodKitchen.interface'
import { ICreateKitchenForm } from 'types/foodLogistics/createKitchenForm.interface'
import { FOOD_DIETARY_TAGS, FOOD_OPERATOR_OPTIONS, FOOD_SUPPLY_MODES } from '../foodData'
import useFocusFirstInput from 'utils/useFocusFirstInput'

interface IProps {
  existingKitchens: IFoodKitchen[]
  closeModal: () => void
  onCreate: (kitchen: IFoodKitchen) => void
}

const emptyForm: ICreateKitchenForm = {
  name: '',
  operator: 'Internal',
  contractor: '',
  supplyMode: 'CLIENT_SUPPLIED',
  caps: [...FOOD_DIETARY_TAGS],
  packout: true,
  softCap: '80',
}

export const CreateKitchenModal = ({ existingKitchens, closeModal, onCreate }: IProps) => {
  const [form, setForm] = useState<ICreateKitchenForm>(emptyForm)
  useModalClose(closeModal)
  useFocusFirstInput()

  const toggleCap = (tag: string) => {
    setForm((f) => ({
      ...f,
      caps: f.caps.includes(tag) ? f.caps.filter((c) => c !== tag) : [...f.caps, tag],
    }))
  }

  const contractorRequired = form.operator === 'Contractor'
  const valid =
    form.name.trim() &&
    form.caps.length > 0 &&
    Number(form.softCap) > 0 &&
    (!contractorRequired || form.contractor.trim())

  const handleCreate = () => {
    const nextId = Math.max(0, ...existingKitchens.map((k) => k.id)) + 1
    onCreate({
      id: nextId,
      name: form.name.trim(),
      caps: form.caps,
      packout: form.packout,
      softCap: Number(form.softCap),
      standbyEnabled: false,
      standby: [],
      operator: form.operator,
      supplyMode: form.supplyMode,
      contractor: contractorRequired ? form.contractor.trim() : undefined,
    })
  }

  return (
    <Modal black width='640px'>
      <Flex column gap='1rem' width='100%'>
        <Flex between width='100%' center>
          <TitleText weight='bold'>Add Kitchen</TitleText>
          <CursorPointer onClick={closeModal}>
            <CloseIcon />
          </CursorPointer>
        </Flex>
        <Input
          labelText='Kitchen name'
          placeholder='e.g. South Kitchen'
          value={form.name}
          onChange={(e: any) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
        <Flex column gap='0.5rem' width='100%'>
          <BodyText weight='600'>Operator</BodyText>
          <Flex gap='0.5rem' width='100%'>
            {FOOD_OPERATOR_OPTIONS.map((o) => (
              <Card
                key={o.key}
                onClick={() => setForm((f) => ({ ...f, operator: o.key }))}
                fullBorder={form.operator === o.key ? colors.orange : undefined}
                hoverBorder
                noScale
              >
                <CardBody>
                  <Flex gap='0.5rem' center>
                    <CircleButton checked={form.operator === o.key} />
                    <Flex column gap='0.125rem'>
                      <BodyText weight='700'>{o.label}</BodyText>
                      <OtherText grey>{o.desc}</OtherText>
                    </Flex>
                  </Flex>
                </CardBody>
              </Card>
            ))}
          </Flex>
        </Flex>
        {contractorRequired && (
          <Input
            labelText='Contractor company'
            placeholder='e.g. Akwaaba Catering Ltd'
            value={form.contractor}
            onChange={(e: any) => setForm((f) => ({ ...f, contractor: e.target.value }))}
            nomarg
          />
        )}
        <Flex column gap='0.5rem' width='100%'>
          <BodyText weight='600'>Supply mode</BodyText>
          <Flex gap='0.5rem' width='100%' column>
            {FOOD_SUPPLY_MODES.map((m) => (
              <Card
                key={m.key}
                onClick={() => setForm((f) => ({ ...f, supplyMode: m.key }))}
                fullBorder={form.supplyMode === m.key ? colors.orange : undefined}
                hoverBorder
                noScale
              >
                <CardBody>
                  <Flex gap='0.5rem' center>
                    <CircleButton checked={form.supplyMode === m.key} />
                    <Flex column gap='0.125rem'>
                      <BodyText weight='700'>{m.label}</BodyText>
                      <OtherText grey>{m.desc}</OtherText>
                    </Flex>
                  </Flex>
                </CardBody>
              </Card>
            ))}
          </Flex>
        </Flex>
        <Flex column gap='0.5rem' width='100%'>
          <BodyText weight='600'>Dietary capability tag set</BodyText>
          <ChipsContainer>
            {FOOD_DIETARY_TAGS.map((t) => (
              <CheckChip key={t} text={t} active={form.caps.includes(t)} onClick={() => toggleCap(t)} />
            ))}
          </ChipsContainer>
        </Flex>
        <Flex gap='1rem' alignEnd wrap width='100%'>
          <CheckBox
            square
            state={form.packout}
            onClick={() => setForm((f) => ({ ...f, packout: !f.packout }))}
            title='Packout support'
          />
          <Flex gap='0.5rem' alignEnd>
            <Input
              type='number'
              labelText='Soft capacity'
              placeholder='e.g. 80'
              value={form.softCap}
              onChange={(e: any) => setForm((f) => ({ ...f, softCap: e.target.value }))}
              width='6rem'
            />
            <OtherText grey style={{ paddingBottom: '0.5rem' }}>
              meals / window
            </OtherText>
          </Flex>
        </Flex>
        <Flex gap='0.5rem' justcenter width='100%'>
          <Button variant='secondary' onClick={closeModal}>Cancel</Button>
          <Button variant='primary' disabled={!valid} onClick={handleCreate}>
            Save
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}
